import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../../components/ui/table';
import { Calendar } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import {
  Users, CheckCircle2, Search, Loader2, Upload, FileText,
  Link2, AlertCircle, ChevronDown, ChevronUp, X, File, Copy, ExternalLink, Clock, Download, CalendarIcon
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ManagerValidationTab() {
  const [stats, setStats] = useState(null);
  const [uploads, setUploads] = useState([]);
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [regionFilter, setRegionFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selectedRegion, setSelectedRegion] = useState('auto');
  const [expandedRow, setExpandedRow] = useState(null);
  const [combinedData, setCombinedData] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [dateRange, setDateRange] = useState({ from: undefined, to: undefined });
  const [datePopoverOpen, setDatePopoverOpen] = useState(false);
  const fileInputRef = useRef(null);

  const token = localStorage.getItem('adminToken') || localStorage.getItem('authToken');
  const headers = { 'Authorization': `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (regionFilter !== 'all') params.append('region', regionFilter);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (search) params.append('search', search);

      const [statsRes, uploadsRes, regionsRes] = await Promise.all([
        fetch(`${API}/api/admin/manager-validation/stats`, { headers }),
        fetch(`${API}/api/admin/manager-validation/uploads?${params}`, { headers }),
        fetch(`${API}/api/admin/manager-validation/regions`, { headers })
      ]);

      if (statsRes.ok) setStats(await statsRes.json());
      if (uploadsRes.ok) {
        const d = await uploadsRes.json();
        setUploads(d.uploads || []);
      }
      if (regionsRes.ok) {
        const d = await regionsRes.json();
        setRegions(d.regions || []);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [token, regionFilter, statusFilter, search]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleFilesSelected = (e) => {
    const files = Array.from(e.target.files || []);
    setSelectedFiles(files);
  };

  const removeFile = (idx) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      toast.error('Please select at least one DOCX file');
      return;
    }

    setUploading(true);
    try {
      if (selectedFiles.length === 1) {
        const formData = new FormData();
        formData.append('file', selectedFiles[0]);
        formData.append('region', selectedRegion);

        const res = await fetch(`${API}/api/admin/manager-validation/upload`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });

        if (res.ok) {
          const data = await res.json();
          const link = `${window.location.origin}${data.assessmentLink}`;
          if (data.matched) {
            toast.success(
              `Uploaded for ${data.matchedEmployee.name}. Link ready to share with ${data.managerFromDoc || 'manager'}.`
            );
          } else {
            toast.warning(`Uploaded but could not auto-match to an employee. Link still generated.`);
          }
          // Auto-copy link
          try {
            await navigator.clipboard.writeText(link);
            toast.info('Assessment link copied to clipboard');
          } catch {}
        } else {
          const err = await res.json();
          toast.error(err.detail || 'Upload failed');
        }
      } else {
        const formData = new FormData();
        selectedFiles.forEach(f => formData.append('files', f));
        formData.append('region', selectedRegion);

        const res = await fetch(`${API}/api/admin/manager-validation/bulk-upload`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });

        if (res.ok) {
          const data = await res.json();
          toast.success(`Uploaded ${data.successCount} of ${data.totalFiles} files. Links generated for each.`);
          if (data.errorCount > 0) {
            toast.warning(`${data.errorCount} files had errors`);
          }
        } else {
          const err = await res.json();
          toast.error(err.detail || 'Bulk upload failed');
        }
      }

      setSelectedFiles([]);
      if (fileInputRef.current) fileInputRef.current.value = '';
      fetchData();
    } catch (e) {
      toast.error('Error uploading files');
    } finally {
      setUploading(false);
    }
  };

  const copyLink = (assessmentToken) => {
    const link = `${window.location.origin}/assessment/${assessmentToken}`;
    navigator.clipboard.writeText(link);
    toast.success('Assessment link copied to clipboard');
  };

  const exportToExcel = async () => {
    try {
      const params = new URLSearchParams();
      if (regionFilter !== 'all') params.append('region', regionFilter);
      if (dateRange.from) params.append('date_from', format(dateRange.from, 'yyyy-MM-dd'));
      if (dateRange.to) params.append('date_to', format(dateRange.to, 'yyyy-MM-dd'));
      const res = await fetch(`${API}/api/admin/manager-validation/export?${params}`, { headers });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `manager_validations_${regionFilter === 'all' ? 'all' : regionFilter}.xlsx`;
        a.click();
        window.URL.revokeObjectURL(url);
        toast.success('Excel exported');
      } else {
        toast.error('Export failed');
      }
    } catch (e) {
      toast.error('Export failed');
    }
  };

  const loadCombinedView = async (employeeId) => {
    if (expandedRow === employeeId) {
      setExpandedRow(null);
      setCombinedData(null);
      return;
    }
    try {
      const res = await fetch(
        `${API}/api/admin/manager-validation/employee-combined/${employeeId}`,
        { headers }
      );
      if (res.ok) {
        setCombinedData(await res.json());
        setExpandedRow(employeeId);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const getStatusBadge = (status) => {
    const cfg = {
      completed: { cls: 'bg-green-100 text-green-700', label: 'Completed' },
      started: { cls: 'bg-blue-100 text-blue-700', label: 'In Progress' },
      sent: { cls: 'bg-yellow-100 text-yellow-700', label: 'Sent' },
      pending: { cls: 'bg-gray-100 text-gray-600', label: 'Pending' }
    };
    const c = cfg[status] || cfg.pending;
    return <Badge className={c.cls}>{c.label}</Badge>;
  };

  const linkedCount = uploads.filter(u => u.isLinked).length;
  const unlinkedCount = uploads.filter(u => !u.isLinked).length;
  const completedCount = uploads.filter(u => u.status === 'completed').length;
  const inProgressCount = uploads.filter(u => u.status === 'started').length;

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="mv-loading">
        <Loader2 className="w-8 h-8 animate-spin text-[#00AEEF]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Manager Validation</h2>
          <p className="text-sm text-gray-500 mt-1">
            Upload manager validation DOCX files to generate assessment links for managers to fill out
          </p>
        </div>
        {uploads.length > 0 && (
          <div className="flex items-center gap-2">
            <Popover open={datePopoverOpen} onOpenChange={setDatePopoverOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" className="text-sm" data-testid="mv-date-filter-btn">
                  <CalendarIcon className="w-4 h-4 mr-2" />
                  {dateRange.from ? (
                    dateRange.to ? (
                      <>{format(dateRange.from, 'MMM d, yyyy')} - {format(dateRange.to, 'MMM d, yyyy')}</>
                    ) : format(dateRange.from, 'MMM d, yyyy')
                  ) : 'Filter by Date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <Calendar
                  mode="range"
                  selected={dateRange}
                  onSelect={(range) => {
                    setDateRange(range || { from: undefined, to: undefined });
                    if (range?.to) setDatePopoverOpen(false);
                  }}
                  numberOfMonths={2}
                  data-testid="mv-date-calendar"
                />
                {dateRange.from && (
                  <div className="px-3 pb-3 flex justify-end">
                    <Button variant="ghost" size="sm" onClick={() => { setDateRange({ from: undefined, to: undefined }); setDatePopoverOpen(false); }}>
                      Clear
                    </Button>
                  </div>
                )}
              </PopoverContent>
            </Popover>
            <Button
              variant="outline"
              onClick={exportToExcel}
              data-testid="mv-export-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Export Excel
            </Button>
          </div>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="mv-stats-grid">
        <Card className={`cursor-pointer transition-shadow hover:shadow-md ${statusFilter === 'all' && regionFilter === 'all' ? 'ring-2 ring-gray-300' : ''}`} onClick={() => { setStatusFilter('all'); setRegionFilter('all'); }}>
          <CardContent className="pt-4 pb-3 px-4">
            <p className="text-xs text-gray-500">Total</p>
            <p className="text-2xl font-bold text-gray-900" data-testid="mv-stat-total">{stats?.total || uploads.length}</p>
          </CardContent>
        </Card>
        <Card className={`cursor-pointer transition-shadow hover:shadow-md ${statusFilter === 'completed' ? 'ring-2 ring-green-400' : ''}`} onClick={() => setStatusFilter('completed')}>
          <CardContent className="pt-4 pb-3 px-4">
            <p className="text-xs text-gray-500">Completed</p>
            <p className="text-2xl font-bold text-green-600" data-testid="mv-stat-completed">{stats?.completed || completedCount}</p>
          </CardContent>
        </Card>
        <Card className={`cursor-pointer transition-shadow hover:shadow-md ${statusFilter === 'started' ? 'ring-2 ring-blue-400' : ''}`} onClick={() => setStatusFilter('started')}>
          <CardContent className="pt-4 pb-3 px-4">
            <p className="text-xs text-gray-500">In Progress</p>
            <p className="text-2xl font-bold text-blue-600" data-testid="mv-stat-inprogress">{stats?.started || inProgressCount}</p>
          </CardContent>
        </Card>
        <Card className={`cursor-pointer transition-shadow hover:shadow-md ${statusFilter === 'pending' ? 'ring-2 ring-gray-400' : ''}`} onClick={() => setStatusFilter('pending')}>
          <CardContent className="pt-4 pb-3 px-4">
            <p className="text-xs text-gray-500">Pending</p>
            <p className="text-2xl font-bold text-amber-600" data-testid="mv-stat-pending">{(stats?.pending || 0) + (stats?.sent || 0) || (uploads.length - completedCount - inProgressCount)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3 px-4">
            <p className="text-xs text-gray-500">Completion Rate</p>
            <p className="text-2xl font-bold text-[#8B5CF6]" data-testid="mv-stat-rate">
              {stats?.total > 0 ? Math.round(((stats?.completed || 0) / stats.total) * 100) : 0}%
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Upload Section */}
      <Card className="border-[#00AEEF]/20" data-testid="mv-upload-section">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Upload className="w-4 h-4 text-[#00AEEF]" />
            Upload Manager Validation Documents
          </CardTitle>
          <CardDescription>
            Upload DOCX files containing manager validation questions. The system will parse the document,
            match it to the corresponding employee, and generate an assessment link for the manager to fill out online.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex flex-wrap gap-4 items-end">
              <div className="w-48">
                <label className="text-sm font-medium text-gray-700 mb-1 block">Region</label>
                <Select value={selectedRegion} onValueChange={setSelectedRegion}>
                  <SelectTrigger data-testid="upload-region-select">
                    <SelectValue placeholder="Select Region" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">All Regions (Auto-detect)</SelectItem>
                    <SelectItem value="jamaica">Jamaica</SelectItem>
                    <SelectItem value="usa">USA</SelectItem>
                    <SelectItem value="canada">Canada</SelectItem>
                    <SelectItem value="barbados">Barbados</SelectItem>
                    <SelectItem value="trinidad_tobago">Trinidad & Tobago</SelectItem>
                    <SelectItem value="panama">Panama</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1">
                <label className="text-sm font-medium text-gray-700 mb-1 block">DOCX Files</label>
                <Input
                  ref={fileInputRef}
                  type="file"
                  accept=".docx"
                  multiple
                  onChange={handleFilesSelected}
                  className="cursor-pointer"
                  data-testid="upload-file-input"
                />
              </div>
              <Button
                onClick={handleUpload}
                disabled={uploading || selectedFiles.length === 0}
                className="bg-[#003366] hover:bg-[#003366]/90"
                data-testid="upload-btn"
              >
                {uploading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4 mr-2" />
                )}
                {uploading ? 'Uploading...' : `Upload ${selectedFiles.length > 0 ? `(${selectedFiles.length})` : ''}`}
              </Button>
            </div>

            {selectedFiles.length > 0 && (
              <div className="flex flex-wrap gap-2" data-testid="selected-files-list">
                {selectedFiles.map((f, i) => (
                  <div key={i} className="flex items-center gap-1 bg-gray-100 rounded px-2 py-1 text-sm">
                    <File className="w-3 h-3 text-gray-500" />
                    <span className="max-w-[200px] truncate">{f.name}</span>
                    <button onClick={() => removeFile(i)} className="ml-1 text-gray-400 hover:text-red-500">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      {uploads.length > 0 || regionFilter !== 'all' || statusFilter !== 'all' || search ? (
        <div className="flex flex-wrap gap-3 items-center" data-testid="mv-filters">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search employee or manager..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9"
              data-testid="mv-search"
            />
          </div>
          <Select value={regionFilter} onValueChange={setRegionFilter}>
            <SelectTrigger className="w-40" data-testid="mv-region-filter">
              <SelectValue placeholder="Region" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Regions</SelectItem>
              {regions.map(r => (
                <SelectItem key={r.region} value={r.region}>
                  {r.region.charAt(0).toUpperCase() + r.region.slice(1)} ({r.count})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40" data-testid="mv-status-filter">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="sent">Sent</SelectItem>
              <SelectItem value="started">In Progress</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
            </SelectContent>
          </Select>
          {(regionFilter !== 'all' || statusFilter !== 'all' || search) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setRegionFilter('all'); setStatusFilter('all'); setSearch(''); }}
              className="text-gray-500 hover:text-gray-700"
              data-testid="mv-clear-filters"
            >
              <X className="w-4 h-4 mr-1" />
              Clear
            </Button>
          )}
          <div className="ml-auto text-sm text-muted-foreground">
            Showing {uploads.length} result{uploads.length !== 1 ? 's' : ''}
          </div>
        </div>
      ) : null}

      {/* Validations Table */}
      {uploads.length > 0 && (
        <Card data-testid="mv-uploads-table">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8"></TableHead>
                  <TableHead>Employee (Being Validated)</TableHead>
                  <TableHead>Manager (Filling Out)</TableHead>
                  <TableHead>Region</TableHead>
                  <TableHead>Progress</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Linked</TableHead>
                  <TableHead>Link</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {uploads.map(u => (
                  <React.Fragment key={u.id}>
                    <TableRow
                      className={`cursor-pointer hover:bg-gray-50 ${u.isLinked ? '' : 'bg-amber-50/50'}`}
                      onClick={() => u.isLinked && u.linkedEmployee && loadCombinedView(u.linkedEmployee.id)}
                      data-testid={`mv-upload-row-${u.id}`}
                    >
                      <TableCell>
                        {u.isLinked && u.linkedEmployee && (
                          expandedRow === u.linkedEmployee.id ?
                            <ChevronUp className="w-4 h-4 text-gray-400" /> :
                            <ChevronDown className="w-4 h-4 text-gray-400" />
                        )}
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium text-sm">{u.employeeName}</p>
                          <p className="text-xs text-gray-500">{u.employeeJobTitle}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="text-sm">{u.managerName}</p>
                          <p className="text-xs text-gray-500">{u.managerEmail}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm capitalize">{u.employeeRegion}</span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="w-16 bg-gray-200 rounded-full h-1.5">
                            <div
                              className={`h-1.5 rounded-full ${u.assessmentProgress === 100 ? 'bg-green-500' : u.assessmentProgress > 0 ? 'bg-blue-500' : 'bg-gray-300'}`}
                              style={{ width: `${u.assessmentProgress || 0}%` }}
                            />
                          </div>
                          <span className="text-xs text-gray-500">{u.assessmentProgress || 0}%</span>
                        </div>
                      </TableCell>
                      <TableCell>{getStatusBadge(u.status)}</TableCell>
                      <TableCell>
                        {u.isLinked ? (
                          <div className="flex items-center gap-1">
                            <Link2 className="w-3 h-3 text-green-600" />
                            <span className="text-xs text-green-700">Linked</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1">
                            <AlertCircle className="w-3 h-3 text-amber-500" />
                            <span className="text-xs text-amber-600">Unlinked</span>
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        {u.assessmentToken && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs"
                            onClick={(e) => { e.stopPropagation(); copyLink(u.assessmentToken); }}
                            data-testid={`copy-link-${u.id}`}
                          >
                            <Copy className="w-3 h-3 mr-1" />
                            Copy Link
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>

                    {expandedRow && u.isLinked && u.linkedEmployee && expandedRow === u.linkedEmployee.id && combinedData && (
                      <TableRow key={`${u.id}-combined`}>
                        <TableCell colSpan={8} className="bg-gray-50 p-4">
                          <CombinedEmployeeView data={combinedData} />
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {uploads.length === 0 && !loading && (
        <Card>
          <CardContent className="py-12 text-center" data-testid="mv-empty-state">
            <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 text-lg">No manager validations yet</p>
            <p className="text-gray-400 text-sm mt-1">
              Upload DOCX validation documents above to generate assessment links for managers
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function CombinedEmployeeView({ data }) {
  if (!data || !data.employee) return null;

  const { employee, managerValidations } = data;
  const sa = employee.selfAssessment;

  return (
    <div className="space-y-4" data-testid="combined-employee-view">
      <div className="flex items-start gap-6">
        <div className="flex-1">
          <h3 className="font-semibold text-gray-900">{employee.name}</h3>
          <p className="text-sm text-gray-500">{employee.jobTitle} | {employee.department}</p>
          <p className="text-xs text-gray-400">{employee.email} | {(employee.region || '').toUpperCase()}</p>
          {employee.managerName && (
            <p className="text-xs text-gray-400 mt-1">Reports to: {employee.managerName}</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Self Assessment Card */}
        <div className="bg-white border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <FileText className="w-4 h-4 text-blue-600" />
            <h4 className="font-medium text-sm text-gray-900">Self-Assessment</h4>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Status</span>
              <Badge className={
                sa.status === 'completed' ? 'bg-green-100 text-green-700' :
                sa.status === 'started' ? 'bg-blue-100 text-blue-700' :
                'bg-gray-100 text-gray-600'
              }>
                {sa.status === 'completed' ? 'Completed' : sa.status === 'started' ? 'In Progress' : 'Pending'}
              </Badge>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Progress</span>
              <span className="font-medium">{sa.progress || 0}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all"
                style={{ width: `${sa.progress || 0}%` }}
              />
            </div>
            {sa.completedAt && (
              <p className="text-xs text-gray-400">
                Completed: {new Date(sa.completedAt).toLocaleDateString()}
              </p>
            )}
          </div>
        </div>

        {/* Manager Validation Card(s) */}
        <div className="bg-white border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle2 className="w-4 h-4 text-green-600" />
            <h4 className="font-medium text-sm text-gray-900">Manager Validation</h4>
          </div>
          {managerValidations.length > 0 ? (
            <div className="space-y-3">
              {managerValidations.map((mv, i) => (
                <div key={i} className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Manager</span>
                    <span className="font-medium">{mv.managerName}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Status</span>
                    <Badge className={
                      mv.status === 'completed' ? 'bg-green-100 text-green-700' :
                      mv.status === 'started' ? 'bg-blue-100 text-blue-700' :
                      'bg-yellow-100 text-yellow-700'
                    }>
                      {mv.status === 'completed' ? 'Completed' :
                       mv.status === 'started' ? 'In Progress' : 'Awaiting'}
                    </Badge>
                  </div>
                  {mv.completedAt && (
                    <p className="text-xs text-gray-400">
                      Completed: {new Date(mv.completedAt).toLocaleDateString()}
                    </p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-400">No manager validation assigned yet</p>
          )}
        </div>
      </div>
    </div>
  );
}
